<!-- eslint-disable perfectionist/sort-modules -->
<!-- eslint-disable perfectionist/sort-imports -->
<script setup lang="ts">
import {
  ChevronDown,
  ChevronUp,
  FunnelX,
  Plus,
  Search,
} from 'lucide-vue-next';
import {
  computed, onMounted, ref,
} from 'vue';

import DashboardLayout from '~/layouts/DashboardLayout.vue';
import ActionButton from '~/components/shared/ActionButton.vue';
import Pagination from '~/components/Pagination';
import useScrollToTop from '~/hooks/useScrollToTop';
import {
  formatSkusValue,
  formatStringValue,
  formatValueWithUnit,
  UNITS,
} from '~/utils/formatters';
import {
  BUTTON_LABELS,
  ENABLED_OPTIONS,
  EQUIPMENT_BUTTON_LABELS,
  EQUIPMENT_EDIT_ROLE,
  EQUIPMENT_LABELS,
  FORM_PLACEHOLDERS,
  MODULE_TABLE_COLUMNS,
  ORDER_BY_OPTIONS,
  STATUS_LABELS,
} from '~/constants/equipment';

import useTableSorting from '../hooks/useTableSorting';
import useModulesFilters from '../hooks/useModulesFilters';
import { useModules } from '../queries/useModulesCrud';
import ModuleModal from '../components/ModuleModal.vue';
import type { Modules } from '~/types/equipment';

const TABLE_COLUMNS = MODULE_TABLE_COLUMNS;

const {
  apiParams,
  filterForm,
  handleReset,
  handleSearch,
  initializeForm,
  setPage,
  setPageSize,
} = useModulesFilters();

const [brandModel, brandProps] = filterForm.defineField('brand');
const [modelModel, modelProps] = filterForm.defineField('model');
const [skuModel, skuProps] = filterForm.defineField('sku');
const [isEnabledModel, isEnabledProps] = filterForm.defineField('is_enabled');
const [orderByModel, orderByProps] = filterForm.defineField('order_by');

const {
  data: modules,
  isLoading: isLoadingModules,
} = useModules(apiParams);

const {
  handleSort,
  sortedModules,
  sortState,
} = useTableSorting(() => modules.value?.data);

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE = 1;
const INPUT_MIN_WIDTHS = {
  BRAND: '140px',
  BUTTONS: '200px',
  ENABLED: '120px',
  MODEL: '140px',
  ORDER_BY: '140px',
  SKU: '140px',
} as const;

const hasNext = computed(() => !!modules.value?.has_next);
const pageSize = computed(() => Number(apiParams.value.page_size) || DEFAULT_PAGE_SIZE);
const currentPage = computed(() => Number(apiParams.value.page) || DEFAULT_PAGE);

function handlePageSizeChange(newPageSize: number): void {
  setPageSize(newPageSize);
}

function handleSetPage(value: number): void {
  const currentPageValue = Number(apiParams.value.page);
  if (hasNext.value || value < currentPageValue) {
    setPage(value);
  }
}

function formatPowerValue(power?: null | number): string {
  return formatValueWithUnit(power, UNITS.KILOWATTS);
}

const isModalVisible = ref(false);
const modalMode = ref<'create' | 'edit' | 'view'>('create');
const selectedModuleId = ref<null | number>(null);
const selectedModuleData = ref<Modules.Module | null>(null);

const handleCreate = () => {
  modalMode.value = 'create';
  selectedModuleId.value = null;
  selectedModuleData.value = null;
  isModalVisible.value = true;
};

const handleView = (module: Modules.Module) => {
  modalMode.value = 'view';
  selectedModuleId.value = module.id;
  selectedModuleData.value = module;
  isModalVisible.value = true;
};

const handleEdit = (module: Modules.Module) => {
  modalMode.value = 'edit';
  selectedModuleId.value = module.id;
  selectedModuleData.value = module;
  isModalVisible.value = true;
};

useScrollToTop(currentPage);

onMounted(() => {
  document.title = 'Módulos';
  initializeForm();
});
</script>

<template>
  <DashboardLayout title="Módulos" description="Lista de módulos disponíveis">
    <div class="row row-cards">
      <div class="col-12">
        <div class="card position-relative">
          <div class="card-header d-flex justify-content-end align-items-end">
            <button
              v-permission="[EQUIPMENT_EDIT_ROLE]"
              type="button"
              class="btn btn-primary"
              @click="handleCreate"
            >
              <Plus class="icon icon-sm me-1" />
              {{ EQUIPMENT_BUTTON_LABELS.CREATE_MODULE }}
            </button>
          </div>
          <div class="card-body border-bottom py-3">
            <div class="d-flex align-items-end gap-2 flex-wrap">
              <div :style="{ minWidth: INPUT_MIN_WIDTHS.BRAND }">
                <div class="mb-3">
                  <label class="form-label" for="brand">{{ EQUIPMENT_LABELS.BRAND }}</label>
                  <input
                    id="brand"
                    v-model="brandModel"
                    v-bind="brandProps"
                    type="text"
                    class="form-control"
                    :placeholder="FORM_PLACEHOLDERS.BRAND_FILTER"
                    @keydown.enter="handleSearch"
                  />
                </div>
              </div>

              <div :style="{ minWidth: INPUT_MIN_WIDTHS.MODEL }">
                <div class="mb-3">
                  <label class="form-label" for="model">{{ EQUIPMENT_LABELS.MODEL }}</label>
                  <input
                    id="model"
                    v-model="modelModel"
                    v-bind="modelProps"
                    type="text"
                    class="form-control"
                    :placeholder="FORM_PLACEHOLDERS.MODEL_FILTER"
                    @keydown.enter="handleSearch"
                  />
                </div>
              </div>

              <div :style="{ minWidth: INPUT_MIN_WIDTHS.SKU }">
                <div class="mb-3">
                  <label class="form-label" for="sku">{{ EQUIPMENT_LABELS.SKUS }}</label>
                  <input
                    id="sku"
                    v-model="skuModel"
                    v-bind="skuProps"
                    type="text"
                    class="form-control"
                    :placeholder="FORM_PLACEHOLDERS.SKU_FILTER"
                    @keydown.enter="handleSearch"
                  />
                </div>
              </div>

              <div :style="{ minWidth: INPUT_MIN_WIDTHS.ENABLED }">
                <div class="mb-3">
                  <label class="form-label" for="isEnabled">{{ EQUIPMENT_LABELS.STATUS }}</label>
                  <select
                    id="isEnabled"
                    v-model="isEnabledModel"
                    v-bind="isEnabledProps"
                    class="form-select"
                  >
                    <option
                      v-for="option in ENABLED_OPTIONS"
                      :key="String(option.value)"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>

              <div :style="{ minWidth: INPUT_MIN_WIDTHS.ORDER_BY }">
                <div class="mb-3">
                  <label class="form-label" for="orderBy">Ordenar por</label>
                  <select
                    id="orderBy"
                    v-model="orderByModel"
                    v-bind="orderByProps"
                    class="form-select"
                  >
                    <option
                      v-for="option in ORDER_BY_OPTIONS"
                      :key="option.value"
                      :value="option.value"
                    >
                      {{ option.label }}
                    </option>
                  </select>
                </div>
              </div>

              <div :style="{ minWidth: INPUT_MIN_WIDTHS.BUTTONS }">
                <div class="mb-3 d-flex gap-2">
                  <button
                    type="button"
                    class="btn btn-primary"
                    @click="handleSearch"
                  >
                    <Search class="icon icon-sm me-1" />
                    {{ BUTTON_LABELS.SEARCH }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-secondary"
                    @click="handleReset"
                  >
                    <FunnelX class="icon icon-sm me-1" />
                    {{ BUTTON_LABELS.CLEAR }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="table-responsive table-with-sticky-actions">
            <table class="table table-vcenter card-table">
              <thead>
                <tr>
                  <th
                    v-for="column in TABLE_COLUMNS.filter(col => col.value !== 'actions')"
                    :key="column.value"
                    :class="{
                      'cursor-pointer': column.sort,
                    }"
                    @click="column.sort ? handleSort(column.value) : null"
                  >
                    <div class="d-flex align-items-center justify-content-between break-anywhere">
                      <span>{{ column.name }}</span>
                      <div v-if="column.sort" class="d-flex flex-column cursor-pointer border rounded-1 mx-2">
                        <ChevronUp
                          class="icon w-3 h-3"
                          @click="handleSort(column.value)"
                          :class="{
                            'text-primary': sortState.column === column.value
                              && sortState.direction === 'asc',
                          }"
                        />
                        <ChevronDown
                          class="icon w-3 h-3"
                          @click="handleSort(column.value)"
                          :class="{
                            'text-primary': sortState.column === column.value
                              && sortState.direction === 'desc',
                          }"
                        />
                      </div>
                    </div>
                  </th>
                  <th class="sticky-actions-column">
                    <span>{{ EQUIPMENT_LABELS.ACTIONS }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-if="!isLoadingModules && sortedModules && sortedModules.length > 0">
                  <tr
                    v-for="module in sortedModules"
                    :key="module.id"
                  >
                    <td>
                      <span class="text-secondary">{{ formatStringValue(module.brand) }}</span>
                    </td>
                    <td>
                      <span class="text-secondary">{{ formatStringValue(module.model) }}</span>
                    </td>
                    <td>
                      <span class="text-secondary">{{ formatPowerValue(module.power) }}</span>
                    </td>
                    <td>
                      <span class="text-secondary skus-value">{{ formatSkusValue(module.skus) }}</span>
                    </td>
                    <td>
                      <span
                        class="badge"
                        :class="module.is_enabled ? 'bg-success-lt' : 'bg-danger-lt'"
                      >
                        {{ module.is_enabled ? STATUS_LABELS.ENABLED : STATUS_LABELS.DISABLED }}
                      </span>
                    </td>
                    <td class="sticky-actions-column">
                      <div class="d-flex gap-2">
                        <ActionButton
                          type="details"
                          @click="handleView(module)"
                        />
                        <ActionButton
                          v-permission="[EQUIPMENT_EDIT_ROLE]"
                          type="edit"
                          @click="handleEdit(module)"
                        />
                      </div>
                    </td>
                  </tr>
                </template>
                <template v-else>
                  <tr>
                    <td :colspan="TABLE_COLUMNS.length">
                      <div class="d-flex justify-content-center">
                        <span>Nenhum módulo encontrado</span>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <Pagination
            :current-page="currentPage"
            :has-next="hasNext"
            :page-size="pageSize"
            @page-change="handleSetPage"
            @page-size-change="handlePageSizeChange"
          />

          <ModuleModal
            v-model="isModalVisible"
            :mode="modalMode"
            :module-data="selectedModuleData"
            :module-id="selectedModuleId"
          />
        </div>
      </div>
    </div>
  </DashboardLayout>
</template>

<style scoped lang="scss">
$min-table-height: 50vh;
$hover-opacity: 0.5;
$actions-column-width: 120px;
$actions-column-bg: #ffffff;
$actions-column-border: #dee2e6;

.table-responsive {
  overflow-x: auto;
  overflow-y: auto;

  &.table-with-sticky-actions {
    position: relative;
  }
}

.card {
  .table-responsive {
    min-height: $min-table-height;
    overflow-x: auto;
    overflow-y: auto;
  }
}

.cursor-pointer {
  cursor: pointer;
}

.break-anywhere {
  word-break: break-word;
}

li {
  &:hover {
    opacity: $hover-opacity;
  }
}

.table {
  tbody {
    tr {
      background-color: white !important;

      &:hover {
        background-color: #f8f9fa !important;
      }
    }
  }

  th,
  td {
    white-space: nowrap;
  }

  .skus-value {
    white-space: pre-line;
  }

  .sticky-actions-column {
    position: sticky;
    right: 0;
    background-color: $actions-column-bg;
    border-left: 1px solid $actions-column-border;
    width: $actions-column-width;
    min-width: $actions-column-width;
    max-width: $actions-column-width;
    z-index: 10;

    tr:hover & {
      background-color: #f8f9fa !important;
    }

    box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1);
  }

  thead .sticky-actions-column {
    background-color: var(--tblr-bg-surface-secondary, #f8f9fa);
    border-bottom: 1px solid $actions-column-border;
  }
}
</style>
